import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/system";
import {
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  InfoOutlined,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  GroupsOutlined as GroupsOutlinedIcon,
  ChildCareOutlined as ChildCareOutlinedIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router";
import DashboardHeader from "../../components/dashboard-header";
import { useNotification } from "../../contexts/NotificationContext";
import { useUserContext } from "../../hooks/userContext";
import { useDashboardFooter } from "../../contexts/DashboardFooterContext";
import { YesNoToggle as YesNoSwitch } from "../homeowner-bio-page/components/shared/Fields";
import { Pages } from "../../utils";
import {
  CREATE_HOMEOWNER_PROJECT,
  GET_HOMEOWNER_PROJECTS,
  UPDATE_HOMEOWNER_PROJECT,
} from "../../graphql/homeowner-project";
import {
  GET_HOMEOWNER_MATCHING_PROFILE,
  UPDATE_HOMEOWNER_MATCHING_PROFILE,
} from "../../graphql/homeowner-matching-profile";
import {
  OTHER_REASON_FOR_HIRING,
  normalizeReasonForHiring,
  PROJECT_PROPERTY_TYPE_OPTIONS,
  PROJECT_SCOPE_OPTIONS,
  PROJECT_SPECIAL_REQUIREMENT_OPTIONS,
  PROPERTY_DETAILS_OPTIONS,
  RESIDENCE_TYPE_OPTIONS,
  OWNERSHIP_DURATION_OPTIONS,
  PROPERTY_LOCATION_OPTIONS as PROPERTY_LOCATION_LABELS,
  SQUARE_FOOTAGE_OPTIONS,
  BEDROOM_OPTIONS,
  BATHROOM_OPTIONS,
  DECISION_MAKER_OPTIONS,
  HOBBY_OPTIONS,
} from "../homeowner-bio-page/data";
import {
  ACQUISITION_PREFS,
  BILLING_METHODS,
  BUDGET_RANGES,
} from "../../components/homeowner-preferences-quiz/constants";
import type { HomeownerProjectRecord } from "../homeowner-bio-page/types";
import type { ExperienceRating, PetEntry } from "../homeowner-bio-page/types";
import ExperienceDialog from "../homeowner-bio-page/components/dialogs/ExperienceDialog";
import PetsDialog from "../homeowner-bio-page/components/dialogs/PetsDialog";
import HouseholdDialog from "../homeowner-bio-page/components/dialogs/HouseholdDialog";
import "./create-project.scss";

type ChoiceOption = { label: string; value: string; helperText?: string };

type ProjectFormState = {
  projectName: string;
  propertyType: string;
  otherPropertyType: string;
  decisionMakers: string;
  workedWithDesigner: boolean;
  experience: { rating: ExperienceRating; notes: string } | null;
  location: {
    address: string;
    address2: string;
    city: string;
    state: string;
    zipcode: string;
  };
  scope: string[];
  otherScope: string;
  specialNeeds: string[];
  otherSpecialNeeds: string;
  startDate: string;
  deadlineDate: string;
  hardDeadline: string;
  budgetRange: string;
  billingPreferences: string[];
  financing: string;
  furniturePreference: string;
  reasonForHiring: string;
  otherReasonForHiring: string;
  propertyDetails: {
    businessType: string;
    rentalType: string;
    residenceType: string;
    ownershipDuration: string;
    squareFootage: string;
    bedrooms: string;
    bathrooms: string;
    propertyLocation: string;
    description: string;
  };
  household: {
    additionalPeople: string;
    peopleTypes: string[];
    childrenAges: string[];
    workFromHome: string;
    hobbies: string[];
    entertain: string;
    pets: string;
    petDetails: PetEntry[];
    guests: string;
    adults: number;
    kids: number;
  };
  wishlist: string;
  goals: string;
};

const SCOPE_LABELS = [
  "Construction/Remodel",
  "Project Management + Design",
  "Full Home Design",
  "Living Room Design",
  "Dining Room Design",
  "Kitchen Design",
  "Primary Bathroom Design",
  "Other Bathroom Design",
  "Primary Bedroom Design",
  "Guest Bedroom Design",
  "Kids Bedroom",
  "Basement Design",
  "Mudroom Design",
  "Entry Design",
  "Outdoor Space Design",
  "Space Planning",
  "Color Consulting",
  "Lighting Design",
  "Furniture Selection",
  "Event/Holiday Decor",
  "Art Selection",
  "Antique Selection",
  "Home Staging",
  "Other",
];

const SPECIAL_NEEDS_OPTIONS: ChoiceOption[] = PROJECT_SPECIAL_REQUIREMENT_OPTIONS.map(
  (option) => ({
    value: option.value,
    label: option.label,
  })
);

const FINANCING_OPTIONS: ChoiceOption[] = [
  { value: "yes", label: "Yes - Interested in Financing" },
  { value: "no", label: "No - Not Interested" },
  { value: "maybe", label: "Maybe - Would like more information" },
];

const FURNITURE_OPTIONS: ChoiceOption[] = ACQUISITION_PREFS.map((option) => ({
  value: option.value,
  label: option.label,
}));

const BILLING_OPTIONS: ChoiceOption[] = BILLING_METHODS.map((option) => ({
  value: option.value,
  label: option.label,
  helperText: option.helperText,
}));

const BUDGET_OPTIONS: ChoiceOption[] = BUDGET_RANGES.map((option) => ({
  value: option.value,
  label: option.label,
}));

const RESIDENCE_OPTIONS: ChoiceOption[] = RESIDENCE_TYPE_OPTIONS.map((label) => ({
  value: label,
  label,
}));

const OWNERSHIP_OPTIONS: ChoiceOption[] = OWNERSHIP_DURATION_OPTIONS.map((label) => ({
  value: label,
  label,
}));

const PROPERTY_LOCATION_OPTIONS_LIST: ChoiceOption[] = PROPERTY_LOCATION_LABELS.map((label) => ({
  value: label,
  label,
}));

const SQUARE_FOOTAGE_OPTIONS_LIST: ChoiceOption[] = SQUARE_FOOTAGE_OPTIONS.map(
  (label) => ({
    value: label,
    label,
  })
);

const BEDROOM_OPTIONS_LIST: ChoiceOption[] = BEDROOM_OPTIONS.map((label) => ({
  value: label,
  label,
}));

const BATHROOM_OPTIONS_LIST: ChoiceOption[] = BATHROOM_OPTIONS.map((label) => ({
  value: label,
  label,
}));

const RENTAL_TYPE_OPTIONS: ChoiceOption[] = PROPERTY_DETAILS_OPTIONS.map((label) => ({
  value: label,
  label,
}));

const MATCHING_PROFILE_SCOPE_VALUE_MAP: Record<string, string> = {
  ENTRY: "ENTRYDESIGN",
  LIGHTING_DESIGN: "LIGHTING",
  EVENT_HOLIDAY_DECOR: "EVENT_DECOR",
};

const US_STATE_OPTIONS = [
  "Select...",
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const normalizeValue = (value: string) => value.trim();

const SectionTitle = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <Box textAlign="center" sx={{ mb: "150px" }}>
    <Typography
      variant="h5"
      fontWeight={700}
      gutterBottom
      sx={{ fontSize: { xs: "20.3px", sm: "1.5rem" } }}
    >
      {title}
    </Typography>
    {subtitle && <Typography color="text.secondary">{subtitle}</Typography>}
  </Box>
);

const ChipSelect = ({
  options,
  value,
  values,
  multiple = false,
  onChange,
  center = false,
  layout = "wrap",
  columns = 3,
  containerSx,
  chipSx,
}: {
  options: ReadonlyArray<ChoiceOption>;
  value?: string;
  values?: string[];
  multiple?: boolean;
  onChange: (nextValue: string | string[]) => void;
  center?: boolean;
  layout?: "wrap" | "grid";
  columns?: number;
  containerSx?: SxProps<Theme>;
  chipSx?: SxProps<Theme>;
}) => {
  const selectedValues = values ?? [];
  const isGrid = layout === "grid";
  return (
    <Box
      className="project-wizard-tag-grid"
      sx={{
        display: isGrid ? "grid" : "flex",
        flexWrap: isGrid ? undefined : "wrap",
        gap: 1.5,
        justifyContent: isGrid ? undefined : center ? "center" : "flex-start",
        width: isGrid ? "100%" : undefined,
        gridTemplateColumns: isGrid
          ? `repeat(${Math.max(columns, 1)}, minmax(0, 1fr))`
          : undefined,
        ...containerSx,
      }}
    >
      {options.map((option) => {
        const selected = multiple
          ? selectedValues.includes(option.value)
          : value === option.value;
        return (
          <Chip
            key={option.value}
            className="project-wizard-tag-chip"
            label={option.label}
            onClick={() => {
              if (multiple) {
                if (selectedValues.includes(option.value)) {
                  onChange(selectedValues.filter((item) => item !== option.value));
                } else {
                  onChange([...selectedValues, option.value]);
                }
              } else {
                onChange(option.value);
              }
            }}
            variant={selected ? "filled" : "outlined"}
            color="secondary"
            sx={{
              fontSize: "0.95rem",
              py: 0.5,
              px: 0.5,
              height: "auto",
              minWidth: "auto",
              width: isGrid ? "100%" : "fit-content",
              borderRadius: isGrid ? "8px" : 50,
              justifyContent: "center",
              "& .MuiChip-label": {
                whiteSpace: "normal",
                textAlign: "center",
                width: "100%",
              },
              ...(isGrid ? { py: 2, px: 4 } : null),
              ...chipSx,
            }}
          />
        );
      })}
    </Box>
  );
};

const normalizeChoiceValue = (
  value: string | null | undefined,
  options: ReadonlyArray<ChoiceOption>
) => {
  if (!value) return "";
  const match = options.find(
    (option) => option.value === value || option.label === value
  );
  return match?.value ?? value;
};

const normalizeChoiceArray = (
  values: string[] | null | undefined,
  options: ReadonlyArray<ChoiceOption>
) => {
  if (!values?.length) return [];
  return values
    .map((value) => normalizeChoiceValue(value, options))
    .filter(Boolean);
};

const removeTypename = <T extends any>(value: any): T => {
  if (Array.isArray(value)) {
    return value.map((item) => removeTypename(item)) as T;
  }
  if (value && typeof value === "object") {
    const cleaned: Record<string, unknown> = {};
    Object.entries(value).forEach(([key, entry]) => {
      if (key === "__typename") return;
      cleaned[key] = removeTypename(entry);
    });
    return cleaned as T;
  }
  return value as T;
};

const normalizeChoiceValueSafe = (
  value: string | null | undefined,
  options: ReadonlyArray<ChoiceOption>
) => {
  const normalized = normalizeChoiceValue(value ?? "", options);
  return options.some((option) => option.value === normalized) ? normalized : "";
};

const filterValidChoices = (
  values: string[],
  options: ReadonlyArray<ChoiceOption>
) => {
  const allowed = new Set(options.map((option) => option.value));
  return values.filter((value) => allowed.has(value));
};

const toYesNo = (value?: boolean | null) => {
  if (value === true) return "yes";
  if (value === false) return "no";
  return "";
};

const toBoolean = (value?: string) => value === "yes";

const PROPERTY_DETAILS_PREFIXES = {
  location: "Location:",
  city: "City:",
  state: "State:",
  zipcode: "Zipcode:",
  otherPropertyType: "Other property type:",
  otherScope: "Other scope:",
  otherSpecialNeeds: "Other special needs:",
  additionalPeople: "Additional people:",
  guests: "Out-of-town guests:",
  workFromHome: "Work from home:",
  entertain: "Entertains regularly:",
  businessType: "Business type:",
  rentalType: "Rental type:",
};

const ZIPCODE_REGEX = /^\d{5}(?:-\d{4})?$/;

const parseLocationValue = (value: string) => {
  const tokens = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (!tokens.length) {
    return {
      address: "",
      address2: "",
      city: "",
      state: "",
      zipcode: "",
    };
  }

  let remaining = [...tokens];
  let zipcode = "";
  let state = "";
  let city = "";

  const last = remaining[remaining.length - 1];
  if (last && ZIPCODE_REGEX.test(last)) {
    zipcode = remaining.pop() ?? "";
    state = remaining.pop() ?? "";
    city = remaining.pop() ?? "";
  } else if (remaining.length >= 3) {
    state = remaining.pop() ?? "";
    city = remaining.pop() ?? "";
  }

  const address = remaining[0] ?? "";
  const address2 = remaining.slice(1).join(", ");

  return {
    address,
    address2,
    city,
    state,
    zipcode,
  };
};

const parsePropertyDetails = (details?: string | null) => {
  if (!details) {
    return {
      location: undefined,
      additionalPeople: "",
      additionalPeopleTypes: [] as string[],
      guests: "",
      rentalType: "",
      otherPropertyType: "",
      otherScope: "",
      otherSpecialNeeds: "",
      description: "",
    };
  }

  const lines = details
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const descriptionLines: string[] = [];
  let location: ReturnType<typeof parseLocationValue> | undefined;
  let locationOverrides: {
    city: string;
    state: string;
    zipcode: string;
  } = {
    city: "",
    state: "",
    zipcode: "",
  };
  let additionalPeople = "";
  let additionalPeopleTypes: string[] = [];
  let guests = "";
  let rentalType = "";
  let otherPropertyType = "";
  let otherScope = "";
  let otherSpecialNeeds = "";

  const getValue = (line: string, prefix: string) => {
    if (line.toLowerCase().startsWith(prefix.toLowerCase())) {
      return line.slice(prefix.length).trim();
    }
    return null;
  };

  for (const line of lines) {
    const locationValue = getValue(line, PROPERTY_DETAILS_PREFIXES.location);
    if (locationValue !== null) {
      location = parseLocationValue(locationValue);
      continue;
    }

    const cityValue = getValue(line, PROPERTY_DETAILS_PREFIXES.city);
    if (cityValue !== null) {
      locationOverrides.city = cityValue;
      continue;
    }

    const stateValue = getValue(line, PROPERTY_DETAILS_PREFIXES.state);
    if (stateValue !== null) {
      locationOverrides.state = stateValue;
      continue;
    }

    const zipcodeValue = getValue(line, PROPERTY_DETAILS_PREFIXES.zipcode);
    if (zipcodeValue !== null) {
      locationOverrides.zipcode = zipcodeValue;
      continue;
    }

    const otherPropertyTypeValue = getValue(
      line,
      PROPERTY_DETAILS_PREFIXES.otherPropertyType
    );
    if (otherPropertyTypeValue !== null) {
      otherPropertyType = otherPropertyTypeValue;
      continue;
    }

    const otherScopeValue = getValue(line, PROPERTY_DETAILS_PREFIXES.otherScope);
    if (otherScopeValue !== null) {
      otherScope = otherScopeValue;
      continue;
    }

    const otherSpecialNeedsValue = getValue(
      line,
      PROPERTY_DETAILS_PREFIXES.otherSpecialNeeds
    );
    if (otherSpecialNeedsValue !== null) {
      otherSpecialNeeds = otherSpecialNeedsValue;
      continue;
    }

    const rentalTypeValue = getValue(line, PROPERTY_DETAILS_PREFIXES.rentalType);
    if (rentalTypeValue !== null) {
      rentalType = rentalTypeValue;
      continue;
    }

    const additionalPeopleValue = getValue(line, PROPERTY_DETAILS_PREFIXES.additionalPeople);
    if (additionalPeopleValue !== null) {
      const lower = additionalPeopleValue.toLowerCase();
      if (lower.startsWith("no")) {
        additionalPeople = "no";
      } else {
        additionalPeople = "yes";
        if (!lower.startsWith("yes")) {
          additionalPeopleTypes = additionalPeopleValue
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        }
      }
      continue;
    }

    const guestsValue = getValue(line, PROPERTY_DETAILS_PREFIXES.guests);
    if (guestsValue !== null) {
      const lower = guestsValue.toLowerCase();
      if (lower.startsWith("yes")) {
        guests = "yes";
      } else if (lower.startsWith("no")) {
        guests = "no";
      }
      continue;
    }

    if (
      getValue(line, PROPERTY_DETAILS_PREFIXES.workFromHome) !== null ||
      getValue(line, PROPERTY_DETAILS_PREFIXES.entertain) !== null ||
      getValue(line, PROPERTY_DETAILS_PREFIXES.businessType) !== null
    ) {
      continue;
    }

    descriptionLines.push(line);
  }

  const hasLocationOverrides = Boolean(
    locationOverrides.city || locationOverrides.state || locationOverrides.zipcode
  );
  if (location || hasLocationOverrides) {
    const baseLocation = location ?? {
      address: "",
      address2: "",
      city: "",
      state: "",
      zipcode: "",
    };
    location = {
      ...baseLocation,
      ...locationOverrides,
    };
  }

  return {
    location,
    additionalPeople,
    additionalPeopleTypes,
    guests,
    otherPropertyType,
    otherScope,
    otherSpecialNeeds,
    rentalType,
    description: descriptionLines.join("\n"),
  };
};

const splitLines = (value: string) =>
  value
    .split(/\n|,/g)
    .map((item) => item.trim())
    .filter(Boolean);

const buildScopeOptions = (): ChoiceOption[] =>
  SCOPE_LABELS.map((label) => {
    const normalizedLabel = label.toLowerCase();
    const match = PROJECT_SCOPE_OPTIONS.find(
      (option) => option.label.toLowerCase() === normalizedLabel
    );
    return {
      label,
      value: match?.value ?? label,
    };
  });

const scopeOptions = buildScopeOptions();

const defaultFormState: ProjectFormState = {
  projectName: "",
  propertyType: "",
  otherPropertyType: "",
  decisionMakers: "",
  workedWithDesigner: false,
  experience: null,
  location: {
    address: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
  },
  scope: [],
  otherScope: "",
  specialNeeds: [],
  otherSpecialNeeds: "",
  startDate: "",
  deadlineDate: "",
  hardDeadline: "no",
  budgetRange: "",
  billingPreferences: [],
  financing: "",
  furniturePreference: "",
  reasonForHiring: "",
  otherReasonForHiring: "",
  propertyDetails: {
    businessType: "",
    rentalType: "",
    residenceType: "",
    ownershipDuration: "",
    squareFootage: "",
    bedrooms: "",
    bathrooms: "",
    propertyLocation: "",
    description: "",
  },
  household: {
    additionalPeople: "no",
    peopleTypes: [],
    childrenAges: ["", "", "", ""],
    workFromHome: "no",
    hobbies: [],
    entertain: "no",
    pets: "no",
    petDetails: [],
    guests: "no",
    adults: 1,
    kids: 0,
  },
  wishlist: "",
  goals: "",
};

const mapProjectToForm = (project: HomeownerProjectRecord): ProjectFormState => {
  const parsedDetails = parsePropertyDetails(project.propertyDetails ?? "");
  const normalizedReason = normalizeReasonForHiring(project.reasonForHiring);
  const peopleTypes = project.household?.adults
    ? splitLines(project.household.adults)
    : [];
  const childrenAges = project.household?.kidsAges
    ? splitLines(project.household.kidsAges)
    : [];
  const hasKids = Boolean(project.household?.kids || childrenAges.length);
  const peopleTypesWithKids =
    hasKids && !peopleTypes.includes("Children")
      ? [...peopleTypes, "Children"]
      : peopleTypes;
  const normalizedChildrenAges = [...childrenAges, "", "", "", ""].slice(0, 4);
  const petDetails = (project.pets ?? [])
    .map((pet) => ({ type: pet.type ?? "", count: pet.count ?? "" }))
    .filter((pet) => pet.type);
  const petsValue =
    project.hasPets === true || petDetails.length
      ? "yes"
      : project.hasPets === false
      ? "no"
      : "";
  const hasHouseholdData = Boolean(
    peopleTypesWithKids.length || childrenAges.length || project.household?.kids
  );
  const additionalPeopleValue =
    hasHouseholdData
      ? "yes"
      : parsedDetails.additionalPeople || "no";
  const parsedPeopleTypes =
    !hasHouseholdData && parsedDetails.additionalPeopleTypes.length
      ? parsedDetails.additionalPeopleTypes
      : peopleTypesWithKids;

  return {
    ...defaultFormState,
    projectName: project.projectName ?? "",
    propertyType: normalizeChoiceValue(
      project.propertyType,
      PROJECT_PROPERTY_TYPE_OPTIONS
    ),
    otherPropertyType: parsedDetails.otherPropertyType ?? "",
    decisionMakers: project.decisionMakers ?? "",
    workedWithDesigner: project.workedWithDesigner ?? false,
    experience: project.experience
      ? {
          rating: project.experience.rating as ExperienceRating,
          notes: project.experience.notes ?? "",
        }
      : null,
    scope: normalizeChoiceArray(project.projectScope, scopeOptions),
    otherScope: parsedDetails.otherScope ?? "",
    specialNeeds: normalizeChoiceArray(
      project.specialRequirements,
      SPECIAL_NEEDS_OPTIONS
    ),
    otherSpecialNeeds: parsedDetails.otherSpecialNeeds ?? "",
    location: {
      ...defaultFormState.location,
      ...(parsedDetails.location ?? {}),
    },
    startDate: project.projectStartDate?.slice(0, 10) ?? "",
    deadlineDate: project.deadlineDate?.slice(0, 10) ?? "",
    hardDeadline: toYesNo(project.hardDeadline) || "no",
    budgetRange: normalizeChoiceValue(project.budgetRange, BUDGET_OPTIONS),
    billingPreferences: normalizeChoiceArray(
      project.billingPreferences,
      BILLING_OPTIONS
    ),
    financing: toYesNo(project.financing),
    furniturePreference: normalizeChoiceValue(
      project.furniturePreferences?.[0] ?? "",
      FURNITURE_OPTIONS
    ),
    reasonForHiring: normalizedReason || "",
    otherReasonForHiring:
      normalizedReason === OTHER_REASON_FOR_HIRING
        ? project.otherReasonForHiring ?? ""
        : "",
    propertyDetails: {
      ...defaultFormState.propertyDetails,
      rentalType: normalizeChoiceValue(parsedDetails.rentalType, RENTAL_TYPE_OPTIONS),
      residenceType: normalizeChoiceValue(project.residenceType, RESIDENCE_OPTIONS),
      ownershipDuration: normalizeChoiceValue(
        project.ownershipDuration,
        OWNERSHIP_OPTIONS
      ),
      squareFootage: normalizeChoiceValue(
        project.squareFootage,
        SQUARE_FOOTAGE_OPTIONS_LIST
      ),
      bedrooms: normalizeChoiceValue(project.bedrooms, BEDROOM_OPTIONS_LIST),
      bathrooms: normalizeChoiceValue(project.bathrooms, BATHROOM_OPTIONS_LIST),
      propertyLocation: normalizeChoiceValue(
        project.propertyLocation,
        PROPERTY_LOCATION_OPTIONS_LIST
      ),
      description: parsedDetails.description ?? "",
    },
    household: {
      ...defaultFormState.household,
      additionalPeople: additionalPeopleValue,
      peopleTypes: parsedPeopleTypes,
      childrenAges: normalizedChildrenAges,
      workFromHome: toYesNo(project.worksRemotely) || "no",
      hobbies: (project.hobbies ?? []).filter(Boolean),
      entertain: toYesNo(project.entertainRegularly) || "no",
      pets: petsValue || "no",
      petDetails,
      guests: parsedDetails.guests || "no",
      adults: Number.parseInt(project.household?.adults ?? "1", 10) || 1,
      kids: Number.parseInt(project.household?.kids ?? "0", 10) || 0,
    },
    wishlist: (project.wishList ?? []).join("\n"),
    goals: project.notes ?? "",
  };
};

type HomeownerProjectCreatePageProps = {
  embedded?: boolean;
  onComplete?: () => void;
  onClose?: () => void;
  projectIdOverride?: string | null;
};

const HomeownerProjectCreatePage = ({
  embedded = false,
  onComplete,
  onClose,
  projectIdOverride = null,
}: HomeownerProjectCreatePageProps) => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { showError, showSuccess } = useNotification();
  const { setFooter, clearFooter } = useDashboardFooter();
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const projectId = projectIdOverride ?? routeProjectId ?? null;
  const isEditMode = Boolean(projectId);
  const homeOwnerId = user?.id ?? (user as { _id?: string | null })?._id ?? "";
  const [formData, setFormData] = useState<ProjectFormState>(defaultFormState);
  const [currentStep, setCurrentStep] = useState(0);
  const [completionState, setCompletionState] = useState<"created" | "updated" | null>(null);
  const [newWishItem, setNewWishItem] = useState("");
  const [experienceModalOpen, setExperienceModalOpen] = useState(false);
  const [experienceDraftRating, setExperienceDraftRating] =
    useState<ExperienceRating>("happy");
  const [experienceDraftNotes, setExperienceDraftNotes] = useState("");
  const [petsModalOpen, setPetsModalOpen] = useState(false);
  const [petDrafts, setPetDrafts] = useState<PetEntry[]>([]);
  const [householdModalOpen, setHouseholdModalOpen] = useState(false);
  const [householdDraft, setHouseholdDraft] = useState({
    adults: 1,
    kids: 0,
    kidsAges: [0, 0, 0, 0],
  });
  const hasHydratedRef = useRef(false);
  const hasPrefilledMatchingProfileRef = useRef(false);
  const wizardBodyRef = useRef<HTMLDivElement | null>(null);

  const [createProject, { loading: isCreating }] = useMutation(
    CREATE_HOMEOWNER_PROJECT,
    {
      refetchQueries: () => {
        if (!homeOwnerId) {
          return [];
        }
        return [
          {
            query: GET_HOMEOWNER_PROJECTS,
            variables: { homeOwnerId },
          },
        ];
      },
    }
  );

  const [updateProject, { loading: isUpdating }] = useMutation(
    UPDATE_HOMEOWNER_PROJECT,
    {
      refetchQueries: () => {
        if (!homeOwnerId) {
          return [];
        }
        return [
          {
            query: GET_HOMEOWNER_PROJECTS,
            variables: { homeOwnerId },
          },
        ];
      },
    }
  );

  const [updateMatchingProfile] = useMutation(UPDATE_HOMEOWNER_MATCHING_PROFILE);

  const { data: projectsData, loading: loadingProjects } = useQuery(
    GET_HOMEOWNER_PROJECTS,
    {
      variables: { homeOwnerId },
      skip: !homeOwnerId,
      fetchPolicy: "cache-and-network",
    }
  );

  const { data: matchingProfileData, loading: loadingMatchingProfile } = useQuery(
    GET_HOMEOWNER_MATCHING_PROFILE,
    {
      variables: { homeOwnerId },
      skip: !homeOwnerId || isEditMode,
      fetchPolicy: "cache-and-network",
    }
  );

  const existingProjects = useMemo(() => {
    return ((projectsData?.getHomeOwnerProjects ?? []).filter(Boolean) ??
      []) as HomeownerProjectRecord[];
  }, [projectsData]);

  const projectToEdit = useMemo(() => {
    if (!isEditMode) return null;
    const projects = (projectsData?.getHomeOwnerProjects ?? []).filter(
      Boolean
    ) as HomeownerProjectRecord[];
    return projects.find((project) => project._id === projectId) ?? null;
  }, [isEditMode, projectId, projectsData]);

  const matchingProfile = matchingProfileData?.getHomeOwnerMatchingProfile ?? null;
  const matchingProfileId = matchingProfile?._id ?? "";
  const hasProjectsData = Boolean(projectsData);
  const isFirstProjectCreation =
    !isEditMode &&
    hasProjectsData &&
    !loadingProjects &&
    existingProjects.length === 0;

  const isSaving = isCreating || isUpdating;

  const propertyTypeValue = formData.propertyType;
  const isRental = propertyTypeValue === "RentalProperty";
  const isCommercial = propertyTypeValue === "SmallCommercial";
  const isPrimaryOrVacation =
    propertyTypeValue === "PrimaryResidence" ||
    propertyTypeValue === "VacationHomeLakeHouse" ||
    propertyTypeValue === "VacationHomeBeachHouse" ||
    propertyTypeValue === "VacationHomeLodge";

  const wishListItems = useMemo(
    () => splitLines(formData.wishlist),
    [formData.wishlist]
  );

  const petLabels = useMemo(() => {
    return formData.household.petDetails.map((pet) => {
      const countValue = Number.parseInt(pet.count, 10);
      const count = Number.isNaN(countValue) ? pet.count : countValue;
      return count ? `${count} ${pet.type}` : pet.type;
    });
  }, [formData.household.petDetails]);

  const householdSummary = useMemo(() => {
    const adults = formData.household.adults;
    const kids = formData.household.kids;
    const ages = formData.household.childrenAges
      .map((age) => age.trim())
      .filter(Boolean);
    return {
      adultsLabel: adults ? `${adults} Adult${adults === 1 ? "" : "s"}` : "",
      kidsLabel: kids ? `${kids} Kid${kids === 1 ? "" : "s"}` : "",
      agesLabel: ages.length ? ages.join(" & ") : "",
    };
  }, [formData.household.adults, formData.household.childrenAges, formData.household.kids]);

  const propertyDetailsTitle = useMemo(() => {
    if (isCommercial) {
      return "Tell us a little bit about your commercial property.";
    }
    if (isRental) {
      return "Tell us a little bit about your rental property.";
    }
    if (isPrimaryOrVacation) {
      return "Tell us a little bit more about your primary residence/vacation home.";
    }
    return "Property Details";
  }, [isCommercial, isPrimaryOrVacation, isRental]);

  const householdTitle = useMemo(() => {
    if (isPrimaryOrVacation) {
      return "How do you like to work and live?";
    }
    return "Household & Lifestyle";
  }, [isPrimaryOrVacation]);

  const steps = useMemo(() => {
    const baseSteps = [
      { id: "project-name", title: "Name your project" },
      { id: "property-type", title: "What is the property type for your project?" },
      {
        id: "location",
        title: "Where is this project located?",
      },
      {
        id: "scope",
        title: "Which of the following best describes your project?",
        subtitle: "(Select All That Apply)",
      },
      {
        id: "special-needs",
        title: "Any special needs or considerations for this project?",
        subtitle: "(Select All That Apply)",
      },
      { id: "timeline", title: "Project Timeline" },
      {
        id: "budget",
        title: "Tell us about your budget range.",
      },
      {
        id: "billing",
        title: "How would you like to be billed for your design project?",
        subtitle: "(Select All That Apply)",
      },
      {
        id: "financing",
        title: "Would you be interested in financing options for your project?",
      },
      { id: "property-details", title: propertyDetailsTitle },
      {
        id: "household",
        title: householdTitle,
        subtitle: isPrimaryOrVacation ? "Working style, shopping, lifestyle." : undefined,
      },
      {
        id: "wishlist",
        title: "Additional details and key considerations.",
        subtitle: "Optional: add any wish list items for your designer.",
      },
      {
        id: "notes",
        title: "Additional notes and key considerations",
        subtitle:
          "Describe your vision and what you hope to achieve with this project.",
      },
    ];

    if (isRental || isCommercial) {
      return baseSteps.filter((step) => step.id !== "household");
    }

    return baseSteps;
  }, [householdTitle, isCommercial, isPrimaryOrVacation, isRental, propertyDetailsTitle]);

  useEffect(() => {
    if (!isEditMode || !projectToEdit || hasHydratedRef.current) {
      return;
    }
    setFormData(mapProjectToForm(projectToEdit));
    hasHydratedRef.current = true;
  }, [isEditMode, projectToEdit]);

  useEffect(() => {
    if (!isFirstProjectCreation || loadingMatchingProfile) {
      return;
    }
    if (hasPrefilledMatchingProfileRef.current) {
      return;
    }

    const matchingProject = matchingProfile?.projectType;
    const matchingPreferences = matchingProfile?.preferences;

    const rawProjectType = matchingProject?.projectType;
    const projectTypeValue = Array.isArray(rawProjectType)
      ? rawProjectType[0]
      : rawProjectType ?? "";
    const propertyType = normalizeChoiceValueSafe(
      projectTypeValue,
      PROJECT_PROPERTY_TYPE_OPTIONS
    );
    const matchingAddress = matchingProject?.projectAddress;

    const scopeValues = filterValidChoices(
      normalizeChoiceArray(
        (matchingProject?.designItems ?? [])
          .filter(Boolean)
          .map((value: string) => MATCHING_PROFILE_SCOPE_VALUE_MAP[value] ?? value),
        scopeOptions
      ),
      scopeOptions
    );

    const specialNeeds = filterValidChoices(
      normalizeChoiceArray(
        (matchingProject?.specialNeeds ?? []).filter(Boolean),
        SPECIAL_NEEDS_OPTIONS
      ),
      SPECIAL_NEEDS_OPTIONS
    );

    const budgetRange = normalizeChoiceValueSafe(
      matchingPreferences?.budgetRange ?? "",
      BUDGET_OPTIONS
    );

    setFormData((prev) => {
      const updates: Partial<ProjectFormState> = {};

      if (propertyType && !prev.propertyType) {
        updates.propertyType = propertyType;
      }
      if (scopeValues.length && prev.scope.length === 0) {
        updates.scope = scopeValues;
      }
      if (specialNeeds.length && prev.specialNeeds.length === 0) {
        updates.specialNeeds = specialNeeds;
      }
      if (budgetRange && !prev.budgetRange) {
        updates.budgetRange = budgetRange;
      }
      if (matchingAddress) {
        const hasLocation = Object.values(prev.location).some((value) =>
          Boolean(value)
        );
        if (!hasLocation) {
          updates.location = {
            ...prev.location,
            address: matchingAddress.streetAddress1 ?? "",
            address2: matchingAddress.streetAddress2 ?? "",
            city: matchingAddress.city ?? "",
            state: matchingAddress.state ?? "",
            zipcode: matchingAddress.zipCode ?? "",
          };
        }
      }

      if (!Object.keys(updates).length) {
        return prev;
      }
      return { ...prev, ...updates };
    });

    hasPrefilledMatchingProfileRef.current = true;
  }, [
    isFirstProjectCreation,
    loadingMatchingProfile,
    matchingProfile,
  ]);

  const updateForm = useCallback(
    (updates: Partial<ProjectFormState>) => {
      setFormData((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  const syncCardMinHeight = useCallback(() => {
    const content = document.querySelector(".dashboard-content") as HTMLElement | null;
    const footer = document.querySelector(
      ".dashboard-sticky-footer"
    ) as HTMLElement | null;
    const body = wizardBodyRef.current;
    if (!content || !body) return;
    if (embedded) {
      body.style.removeProperty("--project-wizard-card-min-height");
      return;
    }
    const contentStyles = window.getComputedStyle(content);
    const bodyStyles = window.getComputedStyle(body);
    const contentHeight = content.getBoundingClientRect().height;
    const paddingTop = Number.parseFloat(contentStyles.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(contentStyles.paddingBottom) || 0;
    const footerHeight = footer?.getBoundingClientRect().height ?? 0;
    const reservedBottom = Math.max(paddingBottom, footerHeight);
    const bodyPadding =
      (Number.parseFloat(bodyStyles.paddingTop) || 0) +
      (Number.parseFloat(bodyStyles.paddingBottom) || 0);
    const minHeight = Math.max(
      0,
      contentHeight - paddingTop - reservedBottom - bodyPadding
    );
    body.style.setProperty(
      "--project-wizard-card-min-height",
      `${minHeight}px`
    );
  }, [embedded]);

  useEffect(() => {
    if (currentStep > steps.length - 1) {
      setCurrentStep(steps.length - 1);
    }
  }, [currentStep, steps.length]);

  useEffect(() => {
    syncCardMinHeight();
    window.addEventListener("resize", syncCardMinHeight);
    return () => window.removeEventListener("resize", syncCardMinHeight);
  }, [syncCardMinHeight]);

  const current = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const updateLocation = useCallback((field: keyof ProjectFormState["location"], value: string) => {
    setFormData((prev) => ({
      ...prev,
      location: { ...prev.location, [field]: value },
    }));
  }, []);

  const updatePropertyDetails = useCallback(
    (field: keyof ProjectFormState["propertyDetails"], value: string) => {
      setFormData((prev) => ({
        ...prev,
        propertyDetails: { ...prev.propertyDetails, [field]: value },
      }));
    },
    []
  );

  const updateHousehold = useCallback(
    (
      field: keyof ProjectFormState["household"],
      value: ProjectFormState["household"][keyof ProjectFormState["household"]]
    ) => {
      setFormData((prev) => ({
        ...prev,
        household: { ...prev.household, [field]: value },
      }));
    },
    []
  );

  const openExperienceDialog = useCallback(() => {
    setExperienceDraftRating(formData.experience?.rating ?? "happy");
    setExperienceDraftNotes(formData.experience?.notes ?? "");
    setExperienceModalOpen(true);
  }, [formData.experience]);

  const saveExperience = useCallback(() => {
    updateForm({
      experience: {
        rating: experienceDraftRating,
        notes: experienceDraftNotes,
      },
    });
    setExperienceModalOpen(false);
  }, [experienceDraftNotes, experienceDraftRating, updateForm]);

  const openPetsDialog = useCallback(() => {
    const initial =
      formData.household.petDetails.length > 0
        ? formData.household.petDetails
        : [{ type: "", count: "1" }];
    setPetDrafts(initial);
    setPetsModalOpen(true);
  }, [formData.household.petDetails]);

  const savePets = useCallback(() => {
    const sanitized = petDrafts.filter((pet) => pet.type);
    updateHousehold("petDetails", sanitized);
    updateHousehold("pets", sanitized.length ? "yes" : "no");
    setPetsModalOpen(false);
  }, [petDrafts, updateHousehold]);

  const openHouseholdDialog = useCallback(() => {
    const kidsAgeValues = formData.household.childrenAges
      .map((age) => Number.parseInt(age, 10))
      .map((value) => (Number.isNaN(value) ? 0 : value));
    const normalizedKidsAges = [...kidsAgeValues, 0, 0, 0, 0].slice(0, 4);
    setHouseholdDraft({
      adults: formData.household.adults || 1,
      kids: formData.household.kids || 0,
      kidsAges: normalizedKidsAges,
    });
    setHouseholdModalOpen(true);
  }, [formData.household.adults, formData.household.childrenAges, formData.household.kids]);

  const adjustHouseholdCount = useCallback(
    (key: "adults" | "kids", delta: number, min = 0) => {
      setHouseholdDraft((prev) => {
        const nextValue = Math.max(min, prev[key] + delta);
        return { ...prev, [key]: nextValue };
      });
    },
    []
  );

  const adjustKidsAge = useCallback((index: number, delta: number) => {
    setHouseholdDraft((prev) => {
      const next = [...prev.kidsAges];
      const nextValue = Math.max(0, next[index] + delta);
      next[index] = nextValue;
      return { ...prev, kidsAges: next };
    });
  }, []);

  const saveHousehold = useCallback(() => {
    updateHousehold("adults", householdDraft.adults);
    updateHousehold("kids", householdDraft.kids);
    const kidsAges = householdDraft.kidsAges
      .slice(0, Math.max(householdDraft.kids, 0))
      .map((age) => String(age))
      .filter((age) => age !== "0");
    updateHousehold(
      "childrenAges",
      [...kidsAges, "", "", "", ""].slice(0, 4)
    );
    updateHousehold(
      "additionalPeople",
      householdDraft.adults > 1 || householdDraft.kids > 0 ? "yes" : "no"
    );
    setHouseholdModalOpen(false);
  }, [householdDraft.adults, householdDraft.kids, householdDraft.kidsAges, updateHousehold]);

  const getValidationError = useCallback(
    (stepId: string) => {
      switch (stepId) {
        case "project-name":
          return normalizeValue(formData.projectName)
            ? null
            : "Please enter a project name.";
        case "property-type":
          if (!formData.propertyType) {
            return "Please select a property type.";
          }
          if (
            formData.propertyType === "Other" &&
            !normalizeValue(formData.otherPropertyType)
          ) {
            return "Please specify the property type.";
          }
          return null;
        case "location":
          if (!normalizeValue(formData.location.address)) {
            return "Please enter the project address.";
          }
          if (!normalizeValue(formData.location.city)) {
            return "Please enter the city.";
          }
          if (!normalizeValue(formData.location.state)) {
            return "Please select a state.";
          }
          if (!normalizeValue(formData.location.zipcode)) {
            return "Please enter the zip code.";
          }
          return null;
        case "scope":
          if (!formData.scope.length) {
            return "Please select at least one project scope.";
          }
          if (
            formData.scope.includes("OTHER") &&
            !normalizeValue(formData.otherScope)
          ) {
            return "Please specify the other project scope.";
          }
          return null;
        case "special-needs":
          if (!formData.specialNeeds.length) {
            return null;
          }
          if (
            formData.specialNeeds.includes("OTHER") &&
            !normalizeValue(formData.otherSpecialNeeds)
          ) {
            return "Please specify the other special need.";
          }
          return null;
        case "timeline":
          if (!formData.startDate) {
            return "Please select a preferred start date.";
          }
          if (!formData.hardDeadline) {
            return "Please tell us if this is a hard deadline.";
          }
          if (formData.hardDeadline === "yes" && !formData.deadlineDate) {
            return "Please select the deadline date.";
          }
          return null;
        case "budget":
          return formData.budgetRange ? null : "Please select a budget range.";
        case "billing":
          return formData.billingPreferences.length
            ? null
            : "Please select at least one billing preference.";
        case "financing":
          return formData.financing
            ? null
            : "Please select a financing preference.";
        case "property-details":
          if (isCommercial) {
            if (!normalizeValue(formData.propertyDetails.businessType)) {
              return "Please tell us the business type.";
            }
            if (!formData.propertyDetails.squareFootage) {
              return "Please select the total square footage.";
            }
            if (!formData.propertyDetails.propertyLocation) {
              return "Please select the property location.";
            }
            return null;
          }
          if (isRental) {
            if (!formData.propertyDetails.residenceType) {
              return "Please select the property type.";
            }
            if (!formData.propertyDetails.rentalType) {
              return "Please select a rental type.";
            }
            if (!formData.propertyDetails.propertyLocation) {
              return "Please select the property location.";
            }
            if (!formData.propertyDetails.squareFootage) {
              return "Please select the total square footage.";
            }
            if (!formData.propertyDetails.bedrooms) {
              return "Please select the number of bedrooms.";
            }
            if (!formData.propertyDetails.bathrooms) {
              return "Please select the number of bathrooms.";
            }
            return null;
          }
          if (isPrimaryOrVacation) {
            if (!formData.propertyDetails.residenceType) {
              return "Please select the property type.";
            }
            if (!formData.propertyDetails.ownershipDuration) {
              return "Please select how long you've owned the property.";
            }
            if (!formData.propertyDetails.squareFootage) {
              return "Please select the total square footage.";
            }
            if (!formData.propertyDetails.bedrooms) {
              return "Please select the number of bedrooms.";
            }
            if (!formData.propertyDetails.bathrooms) {
              return "Please select the number of bathrooms.";
            }
            if (!formData.propertyDetails.propertyLocation) {
              return "Please select the property location.";
            }
            return null;
          }
          return normalizeValue(formData.propertyDetails.description)
            ? null
            : "Please describe your property.";
        case "household":
          if (!formData.furniturePreference) {
            return "Please select a furniture preference.";
          }
          if (!formData.decisionMakers) {
            return "Please select the decision maker(s).";
          }
          if (formData.workedWithDesigner && !formData.experience) {
            return "Please add your designer experience.";
          }
          if (
            formData.household.pets === "yes" &&
            !formData.household.petDetails.length
          ) {
            return "Please add your pets.";
          }
          if (!formData.household.guests) {
            return "Please tell us about out-of-town guests.";
          }
          return null;
        case "wishlist":
          return null;
        case "notes":
          return null;
        default:
          return null;
      }
    },
    [formData, isCommercial, isPrimaryOrVacation, isRental]
  );

  const validateStep = useCallback(
    (stepId: string) => {
      const error = getValidationError(stepId);
      if (error) {
        showError(error);
        return false;
      }
      return true;
    },
    [getValidationError, showError]
  );

  const validateAllSteps = useCallback(() => {
    for (let index = 0; index < steps.length; index += 1) {
      const error = getValidationError(steps[index].id);
      if (error) {
        showError(error);
        setCurrentStep(index);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return false;
      }
    }
    return true;
  }, [getValidationError, showError, steps]);

  const handleNext = useCallback(() => {
    if (currentStep >= steps.length - 1) {
      return;
    }
    if (!current || !validateStep(current.id)) {
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [current, currentStep, steps.length, validateStep]);

  const handleBack = useCallback(() => {
    if (currentStep === 0) {
      if (embedded) {
        onClose?.();
        return;
      }
      navigate(Pages.MY_PROJECTS);
      return;
    }
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep, embedded, navigate, onClose]);

  const resetForm = useCallback(() => {
    setFormData(defaultFormState);
    setCurrentStep(0);
    setCompletionState(null);
    setNewWishItem("");
  }, []);

  const buildPropertyDetails = useCallback(() => {
    const lines: string[] = [];
    const addressParts = [
      normalizeValue(formData.location.address),
      normalizeValue(formData.location.address2),
      normalizeValue(formData.location.city),
      normalizeValue(formData.location.state),
      normalizeValue(formData.location.zipcode),
    ].filter(Boolean);

    if (addressParts.length) {
      lines.push(`Location: ${addressParts.join(", ")}`);
    }

    if (formData.propertyType === "Other" && normalizeValue(formData.otherPropertyType)) {
      lines.push(`Other property type: ${normalizeValue(formData.otherPropertyType)}`);
    }

    if (
      formData.scope.includes("OTHER") &&
      normalizeValue(formData.otherScope)
    ) {
      lines.push(`Other scope: ${normalizeValue(formData.otherScope)}`);
    }

    if (
      formData.specialNeeds.includes("OTHER") &&
      normalizeValue(formData.otherSpecialNeeds)
    ) {
      lines.push(`Other special needs: ${normalizeValue(formData.otherSpecialNeeds)}`);
    }

    const cityValue = normalizeValue(formData.location.city);
    const stateValue = normalizeValue(formData.location.state);
    const zipcodeValue = normalizeValue(formData.location.zipcode);

    if (cityValue) {
      lines.push(`City: ${cityValue}`);
    }
    if (stateValue) {
      lines.push(`State: ${stateValue}`);
    }
    if (zipcodeValue) {
      lines.push(`Zipcode: ${zipcodeValue}`);
    }

    if (isCommercial && formData.propertyDetails.businessType) {
      lines.push(`Business type: ${formData.propertyDetails.businessType}`);
    }

    if (isRental && formData.propertyDetails.rentalType) {
      lines.push(`Rental type: ${formData.propertyDetails.rentalType}`);
    }

    if (formData.propertyDetails.description) {
      lines.push(formData.propertyDetails.description);
    }

    const adultsCount = formData.household.adults;
    const kidsCount = formData.household.kids;
    const additionalPeopleParts: string[] = [];
    if (adultsCount > 1) {
      additionalPeopleParts.push(
        `${adultsCount} Adult${adultsCount === 1 ? "" : "s"}`
      );
    }
    if (kidsCount > 0) {
      additionalPeopleParts.push(
        `${kidsCount} Kid${kidsCount === 1 ? "" : "s"}`
      );
    }
    if (adultsCount || kidsCount) {
      lines.push(
        `Additional people: ${
          additionalPeopleParts.length ? additionalPeopleParts.join(", ") : "No"
        }`
      );
    }

    if (formData.household.guests) {
      lines.push(
        `Out-of-town guests: ${formData.household.guests === "yes" ? "Yes" : "No"}`
      );
    }

    if (formData.household.workFromHome === "yes") {
      lines.push("Work from home: Yes");
    }

    if (formData.household.entertain === "yes") {
      lines.push("Entertains regularly: Yes");
    }

    return lines.join("\n");
  }, [formData, isCommercial, isRental]);

  const handleSubmit = useCallback(async () => {
    if (!validateAllSteps()) {
      return;
    }
    if (!homeOwnerId) {
      showError("Missing homeowner account. Please sign in again.");
      return;
    }

    const hasPets = formData.household.pets === "yes";
    const pets = hasPets
      ? formData.household.petDetails.map((pet) => ({
          type: pet.type,
          count: pet.count ?? "",
        }))
      : [];

    const kidsAges = formData.household.childrenAges
      .map((age) => age.trim())
      .filter(Boolean);

    const household =
      formData.household.adults || formData.household.kids
        ? {
            adults: String(formData.household.adults),
            kids: String(formData.household.kids),
            kidsAges: kidsAges.join(", "),
          }
        : null;

    const propertyDetails = buildPropertyDetails();

    const propertyTypeLabel = PROJECT_PROPERTY_TYPE_OPTIONS.find(
      (option) => option.value === formData.propertyType
    )?.label;

    const derivedProjectName =
      (formData.scope.length
        ? scopeOptions.find((option) => option.value === formData.scope[0])?.label
        : "") ||
      propertyTypeLabel ||
      "New Project";

    const reasonForHiring = formData.reasonForHiring;

    const otherReasonForHiring =
      reasonForHiring === OTHER_REASON_FOR_HIRING
        ? normalizeValue(formData.otherReasonForHiring)
        : "";

    const projectNameToSave =
      normalizeValue(formData.projectName) ||
      (isEditMode && projectToEdit?.projectName
        ? projectToEdit.projectName
        : derivedProjectName);

    const payload = {
      projectName: projectNameToSave,
      projectStartDate: formData.startDate || undefined,
      propertyType: formData.propertyType || undefined,
      projectScope: formData.scope.length ? formData.scope : undefined,
      billingPreferences: formData.billingPreferences.length
        ? formData.billingPreferences
        : undefined,
      budgetRange: formData.budgetRange || undefined,
      specialRequirements: formData.specialNeeds.length
        ? formData.specialNeeds
        : undefined,
      furniturePreferences: formData.furniturePreference
        ? [formData.furniturePreference]
        : undefined,
      hardDeadline: formData.hardDeadline === "yes",
      deadlineDate:
        formData.hardDeadline === "yes" && formData.deadlineDate
          ? formData.deadlineDate
          : undefined,
      financing:
        formData.financing === "yes" || formData.financing === "maybe"
          ? true
          : formData.financing === "no"
          ? false
          : undefined,
      reasonForHiring: reasonForHiring || undefined,
      otherReasonForHiring: otherReasonForHiring || undefined,
      decisionMakers: formData.decisionMakers || undefined,
      workedWithDesigner: formData.workedWithDesigner,
      experience:
        formData.workedWithDesigner && formData.experience
          ? formData.experience
          : undefined,
      ownershipDuration:
        isPrimaryOrVacation && formData.propertyDetails.ownershipDuration
          ? formData.propertyDetails.ownershipDuration
          : undefined,
      propertyDetails: propertyDetails || undefined,
      residenceType: formData.propertyDetails.residenceType || undefined,
      propertyLocation: formData.propertyDetails.propertyLocation || undefined,
      squareFootage: formData.propertyDetails.squareFootage || undefined,
      bedrooms: formData.propertyDetails.bedrooms || undefined,
      bathrooms: formData.propertyDetails.bathrooms || undefined,
      worksRemotely: formData.household.workFromHome === "yes",
      entertainRegularly: formData.household.entertain === "yes",
      hasPets,
      pets: pets.length ? pets : undefined,
      hobbies: formData.household.hobbies.length
        ? formData.household.hobbies
        : undefined,
      household: household || undefined,
      wishList: formData.wishlist ? splitLines(formData.wishlist) : undefined,
      notes: formData.goals || undefined,
    };

    try {
      if (isEditMode && projectId) {
        await updateProject({
          variables: {
            _id: projectId,
            ...payload,
          },
        });
        if (embedded) {
          showSuccess("Project updated successfully.");
          onComplete?.();
          return;
        }
        setCompletionState("updated");
        showSuccess("Project updated successfully.");
      } else {
        const shouldAutoActivateProject = isFirstProjectCreation;
        await createProject({
          variables: {
            homeOwnerId,
            active: shouldAutoActivateProject,
            status: shouldAutoActivateProject ? "active" : undefined,
            ...payload,
          },
        });
        if (isFirstProjectCreation && matchingProfileId) {
          const existingProjectAddress = matchingProfile?.projectType?.projectAddress;
          const projectTypePayload = {
            projectType: formData.propertyType || undefined,
            designItems: formData.scope,
            specialNeeds: formData.specialNeeds,
            projectAddress: {
              id: existingProjectAddress?.id,
              streetAddress1: formData.location.address,
              streetAddress2: formData.location.address2,
              city: formData.location.city,
              state: formData.location.state,
              zipCode: formData.location.zipcode,
            },
          };
          const cleanPreferences = matchingProfile?.preferences
            ? (removeTypename(matchingProfile.preferences) as Record<string, unknown>)
            : undefined;
          const preferencesPayload = {
            ...(cleanPreferences ?? {}),
            budgetRange:
              formData.budgetRange ||
              (cleanPreferences?.budgetRange as string | undefined),
          };
          try {
            await updateMatchingProfile({
              variables: {
                _id: matchingProfileId,
                projectType: projectTypePayload,
                preferences: preferencesPayload,
              },
            });
          } catch (error) {
            const message =
              (error as { graphQLErrors?: Array<{ message?: string }> })
                ?.graphQLErrors?.[0]?.message ||
              (error as Error)?.message ||
              "Project saved, but we couldn't update the matching profile.";
            showError(message);
          }
        }
        if (shouldAutoActivateProject) {
          showSuccess("Project created and activated. Taking you to Matches.");
          onComplete?.();
          navigate(Pages.MATCHES);
          return;
        }
        if (embedded) {
          showSuccess("Project created successfully.");
          onComplete?.();
          return;
        }
        setCompletionState("created");
        showSuccess("Project created successfully.");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      const message =
        (error as { graphQLErrors?: Array<{ message?: string }> })
          ?.graphQLErrors?.[0]?.message ||
        (error as Error)?.message ||
        "We couldn't save your project. Please try again.";
      showError(message);
    }
  }, [
    buildPropertyDetails,
    createProject,
    formData,
    isPrimaryOrVacation,
    isEditMode,
    projectId,
    projectToEdit,
    updateProject,
    showError,
    showSuccess,
    homeOwnerId,
    validateAllSteps,
    isFirstProjectCreation,
    matchingProfile,
    matchingProfileId,
    updateMatchingProfile,
    embedded,
    onComplete,
    navigate,
  ]);

  useEffect(() => {
    if (
      embedded ||
      completionState ||
      (isEditMode && (loadingProjects || !projectToEdit))
    ) {
      clearFooter();
      return;
    }
    setFooter(
      <>
        <Button
          onClick={handleBack}
          variant="text"
          startIcon={<ChevronLeft sx={{ width: 16, height: 16 }} />}
          sx={{ fontSize: { xs: "14px", sm: "16px" } }}
          disabled={isSaving}
        >
          Back
        </Button>
        <Button
          onClick={isLastStep ? handleSubmit : handleNext}
          variant="contained"
          endIcon={
            isLastStep ? (
              isSaving ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : undefined
            ) : (
              <ChevronRight sx={{ width: 16, height: 16 }} />
            )
          }
          disabled={isSaving}
          sx={{ fontSize: { xs: "14px", sm: "16px" } }}
        >
          {isLastStep
            ? isEditMode
              ? "Save Changes"
              : "Create Project"
            : `Continue ${currentStep + 1} of ${steps.length}`}
        </Button>
      </>
    );
    requestAnimationFrame(syncCardMinHeight);
    return () => clearFooter();
  }, [
    clearFooter,
    completionState,
    currentStep,
    handleBack,
    handleNext,
    handleSubmit,
    isEditMode,
    isLastStep,
    isSaving,
    loadingProjects,
    projectToEdit,
    setFooter,
    syncCardMinHeight,
    steps.length,
    embedded,
  ]);

  if (isEditMode && loadingProjects) {
    return (
      <Box className="project-wizard-page">
        {!embedded && <DashboardHeader title="Edit Project" showBackButton />}
        <Box className="project-wizard-body">
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: "#00A06D" }} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (isEditMode && !loadingProjects && !projectToEdit) {
    return (
      <Box className="project-wizard-page">
        {!embedded && <DashboardHeader title="Edit Project" showBackButton />}
        <Box className="project-wizard-body">
          <Alert severity="error">
            We couldn't find that project. Please return to your projects list.
          </Alert>
        </Box>
      </Box>
    );
  }

  if (completionState) {
    return (
      <Box className="project-wizard-page">
        {!embedded && (
          <DashboardHeader
            title={completionState === "updated" ? "Project Updated" : "Project Created"}
            showBackButton
          />
        )}
        <Box className="project-wizard-body">
          <Paper className="project-wizard-card" elevation={0}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Box className="project-wizard-success">Success</Box>
              <Box>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {completionState === "updated" ? "Project Updated!" : "Project Created!"}
                </Typography>
                <Typography color="text.secondary">
                  {completionState === "updated"
                    ? "Your project details have been saved."
                    : "Your project has been created in draft status."}
                </Typography>
              </Box>
              <Alert severity="success" sx={{ width: "100%", textAlign: "left" }}>
                {completionState === "updated"
                  ? "You can review your updated project details or return to your project list."
                  : "You can review your project details, activate it to start receiving designer matches, or create another project."}
              </Alert>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(Pages.MY_PROJECTS)}
                >
                  View My Projects
                </Button>
                {completionState === "created" && (
                  <Button variant="outlined" onClick={resetForm}>
                    Create Another Project
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="project-wizard-page">
      {!embedded && (
        <DashboardHeader
          title={isEditMode ? "Edit Project" : "Create Project"}
          showBackButton
          containerSx={{ pb: 0 }}
        />
      )}
      <Box className="project-wizard-body" ref={wizardBodyRef}>
        <Paper className="project-wizard-card" elevation={0}>
          <Stack spacing={4}>
            {current && (
              <SectionTitle title={current.title} subtitle={current.subtitle} />
            )}

            {current?.id === "project-name" && (
              <Box maxWidth={520} mx="auto">
                <TextField
                  label="Project Name"
                  value={formData.projectName}
                  onChange={(event) => updateForm({ projectName: event.target.value })}
                  fullWidth
                />
              </Box>
            )}

            {current?.id === "property-type" && (
              <Container maxWidth="md">
                <ChipSelect
                  options={PROJECT_PROPERTY_TYPE_OPTIONS}
                  value={formData.propertyType}
                  onChange={(value) => {
                    const nextValue = value as string;
                    updateForm({
                      propertyType: nextValue,
                      ...(nextValue !== "Other" ? { otherPropertyType: "" } : {}),
                    });
                  }}
                  center
                />
                {formData.propertyType === "Other" && (
                  <Box mt={4} display="flex" justifyContent="center">
                    <Box width="100%" maxWidth="500px">
                      <Typography
                        variant="body2"
                        mb={1}
                        sx={{ color: "text.secondary" }}
                      >
                        Please specify
                      </Typography>
                      <Typography
                        variant="body2"
                        mb={1}
                        sx={{ color: "text.secondary" }}
                      >
                        Other
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter property type"
                        value={formData.otherPropertyType}
                        onChange={(event) =>
                          updateForm({ otherPropertyType: event.target.value })
                        }
                        variant="standard"
                        sx={{
                          "& .MuiInput-root:before": {
                            borderBottom: "1px solid",
                            borderBottomColor: "secondary.main",
                          },
                          "& .MuiInput-root:hover:before": {
                            borderBottomColor: "secondary.main",
                          },
                          "& .MuiInput-root:after": {
                            borderBottomColor: "secondary.main",
                          },
                          "& .MuiInputBase-root": {
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            borderRadius: 0,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Container>
            )}

            {current?.id === "location" && (
              <Container maxWidth="md">
                <Stack spacing={2}>
                  <TextField
                    label="Mailing Address"
                    value={formData.location.address}
                    onChange={(event) => updateLocation("address", event.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Apt, Suite, Floor, etc. (Optional)"
                    value={formData.location.address2}
                    onChange={(event) => updateLocation("address2", event.target.value)}
                    fullWidth
                  />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 0 } }}>
                      <TextField
                        label="City"
                        value={formData.location.city}
                        onChange={(event) => updateLocation("city", event.target.value)}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 0 } }}>
                      <TextField
                        label="State"
                        select
                        value={formData.location.state}
                        onChange={(event) =>
                          updateLocation("state", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        {US_STATE_OPTIONS.map((option) => (
                          <option
                            key={option}
                            value={option === "Select..." ? "" : option}
                          >
                            {option}
                          </option>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 0 } }}>
                      <TextField
                        label="Zipcode"
                        value={formData.location.zipcode}
                        onChange={(event) =>
                          updateLocation("zipcode", event.target.value)
                        }
                        fullWidth
                      />
                    </Box>
                  </Box>
                </Stack>
              </Container>
            )}

            {current?.id === "scope" && (
              <Container maxWidth="md">
                <ChipSelect
                  options={scopeOptions}
                  values={formData.scope}
                  multiple
                  onChange={(value) => {
                    const nextValues = value as string[];
                    updateForm({
                      scope: nextValues,
                      ...(nextValues.includes("OTHER")
                        ? {}
                        : { otherScope: "" }),
                    });
                  }}
                  center
                />
                {formData.scope.includes("OTHER") && (
                  <Box mt={4} display="flex" justifyContent="center">
                    <Box width="100%" maxWidth="500px">
                      <Typography
                        variant="body2"
                        mb={1}
                        sx={{ color: "text.secondary" }}
                      >
                        Please specify
                      </Typography>
                      <Typography
                        variant="body2"
                        mb={1}
                        sx={{ color: "text.secondary" }}
                      >
                        Other
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter project scope"
                        value={formData.otherScope}
                        onChange={(event) =>
                          updateForm({ otherScope: event.target.value })
                        }
                        variant="standard"
                        sx={{
                          "& .MuiInput-root:before": {
                            borderBottom: "1px solid",
                            borderBottomColor: "secondary.main",
                          },
                          "& .MuiInput-root:hover:before": {
                            borderBottomColor: "secondary.main",
                          },
                          "& .MuiInput-root:after": {
                            borderBottomColor: "secondary.main",
                          },
                          "& .MuiInputBase-root": {
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            borderRadius: 0,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Container>
            )}

            {current?.id === "special-needs" && (
              <Container maxWidth="md">
                <ChipSelect
                  options={SPECIAL_NEEDS_OPTIONS}
                  values={formData.specialNeeds}
                  multiple
                  onChange={(value) => {
                    const nextValues = value as string[];
                    updateForm({
                      specialNeeds: nextValues,
                      ...(nextValues.includes("OTHER")
                        ? {}
                        : { otherSpecialNeeds: "" }),
                    });
                  }}
                  center
                />
                {formData.specialNeeds.includes("OTHER") && (
                  <Box mt={4} display="flex" justifyContent="center">
                    <Box width="100%" maxWidth="500px">
                      <Typography
                        variant="body2"
                        mb={1}
                        sx={{ color: "text.secondary" }}
                      >
                        Please specify
                      </Typography>
                      <Typography
                        variant="body2"
                        mb={1}
                        sx={{ color: "text.secondary" }}
                      >
                        Other
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter special need"
                        value={formData.otherSpecialNeeds}
                        onChange={(event) =>
                          updateForm({ otherSpecialNeeds: event.target.value })
                        }
                        variant="standard"
                        sx={{
                          "& .MuiInput-root:before": {
                            borderBottom: "1px solid",
                            borderBottomColor: "secondary.main",
                          },
                          "& .MuiInput-root:hover:before": {
                            borderBottomColor: "secondary.main",
                          },
                          "& .MuiInput-root:after": {
                            borderBottomColor: "secondary.main",
                          },
                          "& .MuiInputBase-root": {
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            borderRadius: 0,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Container>
            )}

            {current?.id === "timeline" && (
              <Container maxWidth="md">
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Please select your preferred project start date.
                    </Typography>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={formData.startDate}
                      onChange={(event) => updateForm({ startDate: event.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      sx={{ "& .MuiInputBase-root": { borderRadius: 3 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Is this a hard deadline?
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <YesNoSwitch
                        value={toBoolean(formData.hardDeadline)}
                        onChange={(value) =>
                          updateForm({ hardDeadline: value ? "yes" : "no" })
                        }
                      />
                      {formData.hardDeadline === "yes" && (
                        <Box flex={1} minWidth={{ xs: "100%", sm: 220 }}>
                          <TextField
                            label="Deadline Date"
                            type="date"
                            value={formData.deadlineDate}
                            onChange={(event) =>
                              updateForm({ deadlineDate: event.target.value })
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            sx={{ "& .MuiInputBase-root": { borderRadius: 3 } }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            )}

            {current?.id === "budget" && (
              <Box maxWidth={520} mx="auto">
                <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                  Select a budget range
                </Typography>
                <TextField
                  select
                  value={formData.budgetRange}
                  onChange={(event) => updateForm({ budgetRange: event.target.value })}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ "& .MuiInputBase-root": { borderRadius: 3 } }}
                >
                  <option value="">Select a budget range</option>
                  {BUDGET_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
              </Box>
            )}

            {current?.id === "billing" && (
              <Container maxWidth="lg">
                <Grid container spacing={2}>
                  {BILLING_OPTIONS.map((option) => {
                    const selected = formData.billingPreferences.includes(
                      option.value
                    );
                    return (
                      <Grid item xs={12} sm={6} md={4} key={option.value}>
                        <Card
                          onClick={() => {
                            const next = selected
                              ? formData.billingPreferences.filter(
                                  (item) => item !== option.value
                                )
                              : [...formData.billingPreferences, option.value];
                            updateForm({ billingPreferences: next });
                          }}
                          sx={{
                            cursor: "pointer",
                            height: "100%",
                            minHeight: 140,
                            border: selected ? "2px solid" : "1px solid",
                            borderColor: selected ? "secondary.main" : "divider",
                            backgroundColor: selected
                              ? "secondary.main"
                              : "transparent",
                            boxShadow: "none",
                            "&:hover": {
                              borderColor: "secondary.main",
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 3,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "flex-start",
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="flex-start"
                              justifyContent="space-between"
                              gap={1}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: "bold",
                                  color: selected ? "#FFFFFF" : "secondary.main",
                                  textAlign: "left",
                                  flex: 1,
                                }}
                              >
                                {option.label}
                              </Typography>
                              {option.helperText && (
                                <Tooltip
                                  title={
                                    <span style={{ whiteSpace: "pre-line" }}>
                                      {option.helperText}
                                    </span>
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    sx={{
                                      color: selected
                                        ? "#FFFFFF"
                                        : "secondary.main",
                                    }}
                                  >
                                    <InfoOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Container>
            )}

            {current?.id === "financing" && (
              <Container maxWidth="md">
                <Grid container spacing={2}>
                  {FINANCING_OPTIONS.map((option) => {
                    const selected = formData.financing === option.value;
                    return (
                      <Grid item xs={12} sm={4} key={option.value}>
                        <Card
                          onClick={() =>
                            updateForm({ financing: option.value })
                          }
                          sx={{
                            cursor: "pointer",
                            height: "100%",
                            minHeight: 80,
                            border: selected ? "2px solid" : "1px solid",
                            borderColor: selected ? "secondary.main" : "divider",
                            backgroundColor: selected
                              ? "secondary.main"
                              : "transparent",
                            boxShadow: "none",
                            "&:hover": {
                              borderColor: "secondary.main",
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          <CardActionArea sx={{ height: "100%" }}>
                            <CardContent>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  textAlign: "center",
                                  color: selected
                                    ? "#FFFFFF"
                                    : "secondary.main",
                                }}
                              >
                                {option.label}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Container>
            )}

            {current?.id === "property-details" && (
              <Stack spacing={3}>
                {isCommercial && (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        label="What type of business is it?"
                        value={formData.propertyDetails.businessType}
                        onChange={(event) =>
                          updatePropertyDetails("businessType", event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="What type of area is the property in?"
                        select
                        value={formData.propertyDetails.propertyLocation}
                        onChange={(event) =>
                          updatePropertyDetails("propertyLocation", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {PROPERTY_LOCATION_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Total square footage"
                        select
                        value={formData.propertyDetails.squareFootage}
                        onChange={(event) =>
                          updatePropertyDetails("squareFootage", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {SQUARE_FOOTAGE_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                )}

                {isRental && (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        label="Residence Type"
                        select
                        value={formData.propertyDetails.residenceType}
                        onChange={(event) =>
                          updatePropertyDetails("residenceType", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {RESIDENCE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" mb={1}>
                        What type of rentals do you accept?
                      </Typography>
                      <ChipSelect
                        options={RENTAL_TYPE_OPTIONS}
                        value={formData.propertyDetails.rentalType}
                        onChange={(value) =>
                          updatePropertyDetails("rentalType", value as string)
                        }
                        layout="grid"
                        columns={3}
                        chipSx={{ py: 2, px: 4, borderRadius: "8px" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="What type of area is the property in?"
                        select
                        value={formData.propertyDetails.propertyLocation}
                        onChange={(event) =>
                          updatePropertyDetails("propertyLocation", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {PROPERTY_LOCATION_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Total square footage"
                        select
                        value={formData.propertyDetails.squareFootage}
                        onChange={(event) =>
                          updatePropertyDetails("squareFootage", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {SQUARE_FOOTAGE_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Number of Bedrooms"
                        select
                        value={formData.propertyDetails.bedrooms}
                        onChange={(event) =>
                          updatePropertyDetails("bedrooms", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {BEDROOM_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Number of Bathrooms"
                        select
                        value={formData.propertyDetails.bathrooms}
                        onChange={(event) =>
                          updatePropertyDetails("bathrooms", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {BATHROOM_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                )}

                {isPrimaryOrVacation && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="What type of residence is this?"
                        select
                        value={formData.propertyDetails.residenceType}
                        onChange={(event) =>
                          updatePropertyDetails("residenceType", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {RESIDENCE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="How long have you lived/owned the property?"
                        select
                        value={formData.propertyDetails.ownershipDuration}
                        onChange={(event) =>
                          updatePropertyDetails(
                            "ownershipDuration",
                            event.target.value
                          )
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {OWNERSHIP_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="What is the total livable square footage?"
                        select
                        value={formData.propertyDetails.squareFootage}
                        onChange={(event) =>
                          updatePropertyDetails("squareFootage", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {SQUARE_FOOTAGE_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="How many bedrooms?"
                        select
                        value={formData.propertyDetails.bedrooms}
                        onChange={(event) =>
                          updatePropertyDetails("bedrooms", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {BEDROOM_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="How many bathrooms?"
                        select
                        value={formData.propertyDetails.bathrooms}
                        onChange={(event) =>
                          updatePropertyDetails("bathrooms", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {BATHROOM_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Where is this property located?"
                        select
                        value={formData.propertyDetails.propertyLocation}
                        onChange={(event) =>
                          updatePropertyDetails("propertyLocation", event.target.value)
                        }
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      >
                        <option value="">Select...</option>
                        {PROPERTY_LOCATION_OPTIONS_LIST.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                )}

                {!isCommercial && !isRental && !isPrimaryOrVacation && (
                  <TextField
                    label="Please describe your property"
                    value={formData.propertyDetails.description}
                    onChange={(event) =>
                      updatePropertyDetails("description", event.target.value)
                    }
                    fullWidth
                    multiline
                    minRows={4}
                  />
                )}
              </Stack>
            )}

            {current?.id === "household" && (
              <Stack spacing={4}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Furniture Selection & Buying Preference
                    </Typography>
                    <TextField
                      select
                      value={formData.furniturePreference}
                      onChange={(event) =>
                        updateForm({ furniturePreference: event.target.value })
                      }
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    >
                      <option value="">Select...</option>
                      {FURNITURE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Who is the project decision maker(s)?
                    </Typography>
                    <TextField
                      select
                      value={formData.decisionMakers}
                      onChange={(event) =>
                        updateForm({ decisionMakers: event.target.value })
                      }
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    >
                      <option value="">Decision Maker(s) *</option>
                      {DECISION_MAKER_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Does anyone work remotely?
                    </Typography>
                    <YesNoSwitch
                      value={toBoolean(formData.household.workFromHome)}
                      onChange={(value) =>
                        updateHousehold("workFromHome", value ? "yes" : "no")
                      }
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Have you worked with a Designer in the Past?
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <YesNoSwitch
                        value={formData.workedWithDesigner}
                        onChange={(value) => {
                          updateForm({ workedWithDesigner: value });
                          if (!value) {
                            updateForm({ experience: null });
                          }
                        }}
                      />
                      {formData.workedWithDesigner && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={openExperienceDialog}
                          sx={{
                            textTransform: "none",
                            textDecoration: "underline",
                          }}
                        >
                          {formData.experience ? "Edit Rating" : "Rate Your Experience"}
                        </Button>
                      )}
                    </Box>
                    {formData.workedWithDesigner && formData.experience?.notes && (
                      <Box
                        sx={{
                          border: "1px solid #E3E7EF",
                          borderRadius: 2,
                          p: 1.5,
                          mt: 1,
                          backgroundColor: "#F7F8FB",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontStyle: "italic", color: "#6B7280" }}>
                          {`"${formData.experience.notes}"`}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Do you entertain on a regular basis?
                    </Typography>
                    <YesNoSwitch
                      value={toBoolean(formData.household.entertain)}
                      onChange={(value) =>
                        updateHousehold("entertain", value ? "yes" : "no")
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Do you have any pets?
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <YesNoSwitch
                        value={toBoolean(formData.household.pets)}
                        onChange={(value) => {
                          updateHousehold("pets", value ? "yes" : "no");
                          if (!value) {
                            updateHousehold("petDetails", []);
                          }
                        }}
                      />
                      {formData.household.pets === "yes" && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={openPetsDialog}
                          sx={{
                            textTransform: "none",
                            textDecoration: "underline",
                          }}
                        >
                          Add/Remove Pets
                        </Button>
                      )}
                    </Box>
                    {petLabels.length > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                        {petLabels.map((label) => (
                          <Chip key={label} label={label} size="small" />
                        ))}
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Tell us about your hobbies and special interest.
                    </Typography>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={HOBBY_OPTIONS}
                      value={formData.household.hobbies}
                      onChange={(_event, value) =>
                        updateHousehold("hobbies", value as string[])
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option}
                            label={option}
                            size="small"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Hobbies & Interests"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      How many people live in your household?
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openHouseholdDialog}
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#1C1C1C",
                          borderRadius: "12px",
                          py: 1,
                          px: 2,
                          fontWeight: 600,
                          "&:hover": { backgroundColor: "#1C1C1C" },
                        }}
                      >
                        Add
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={openHouseholdDialog}
                        sx={{
                          textTransform: "none",
                          textDecoration: "underline",
                          color: "secondary.main",
                          fontWeight: 600,
                        }}
                      >
                        Edit Household
                      </Button>
                    </Box>
                    <Box mt={2}>
                      {householdSummary.adultsLabel && (
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                          <GroupsOutlinedIcon fontSize="small" />
                          <Typography variant="body2">{householdSummary.adultsLabel}</Typography>
                        </Stack>
                      )}
                      {householdSummary.kidsLabel && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ChildCareOutlinedIcon fontSize="small" />
                          <Typography variant="body2">
                            {householdSummary.kidsLabel}
                            {householdSummary.agesLabel
                              ? ` Ages: ${householdSummary.agesLabel}`
                              : ""}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Will you have out of town guests that stay with you at this property?
                  </Typography>
                  <YesNoSwitch
                    value={toBoolean(formData.household.guests)}
                    onChange={(value) =>
                      updateHousehold("guests", value ? "yes" : "no")
                    }
                  />
                </Box>
              </Stack>
            )}

            {current?.id === "wishlist" && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} mb={1}>
                    Client Wish List
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    List specific features or requirements the client wanted (e.g., “Marble countertops”, “LED lighting”)
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  {wishListItems.map((item, index) => (
                    <Box
                      key={`${item}-${index}`}
                      className="project-wishlist-item"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckIcon fontSize="small" />
                        <Typography variant="body2">{item}</Typography>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const next = wishListItems.filter(
                            (_entry, entryIndex) => entryIndex !== index
                          );
                          updateForm({ wishlist: next.join("\n") });
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <TextField
                    value={newWishItem}
                    onChange={(event) => setNewWishItem(event.target.value)}
                    placeholder="Enter wish list item"
                    size="small"
                    fullWidth
                    onKeyPress={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        const trimmed = newWishItem.trim();
                        if (!trimmed) return;
                        const next = [...wishListItems, trimmed];
                        updateForm({ wishlist: next.join("\n") });
                        setNewWishItem("");
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CheckIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setNewWishItem("")}
                            disabled={!newWishItem}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    onClick={() => {
                      const trimmed = newWishItem.trim();
                      if (!trimmed) return;
                      const next = [...wishListItems, trimmed];
                      updateForm({ wishlist: next.join("\n") });
                      setNewWishItem("");
                    }}
                    variant="outlined"
                    disabled={!newWishItem.trim()}
                    sx={{
                      borderColor: "#E25A8A",
                      borderStyle: "dashed",
                      borderWidth: 1,
                      borderRadius: 2,
                      color: "#C2185B",
                      width: "100%",
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#C2185B",
                        backgroundColor: "rgba(194, 24, 91, 0.04)",
                      },
                    }}
                  >
                    + Add Wish List Item
                  </Button>
                </Stack>
              </Stack>
            )}

            {current?.id === "notes" && (
              <Container maxWidth="md" sx={{ width: "100%" }}>
                <TextField
                  label="Additional notes"
                  value={formData.goals}
                  onChange={(event) => updateForm({ goals: event.target.value })}
                  multiline
                  minRows={6}
                  fullWidth
                />
              </Container>
            )}
          </Stack>
        </Paper>
        {embedded && (
          <Box className="project-wizard-embedded-actions">
            <Button
              onClick={handleBack}
              variant="text"
              startIcon={<ChevronLeft sx={{ width: 16, height: 16 }} />}
              sx={{ fontSize: { xs: "14px", sm: "16px" } }}
              disabled={isSaving}
            >
              Back
            </Button>
            <Button
              onClick={isLastStep ? handleSubmit : handleNext}
              variant="contained"
              endIcon={
                isLastStep ? (
                  isSaving ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : undefined
                ) : (
                  <ChevronRight sx={{ width: 16, height: 16 }} />
                )
              }
              disabled={isSaving}
              sx={{ fontSize: { xs: "14px", sm: "16px" } }}
            >
              {isLastStep
                ? isEditMode
                  ? "Save Changes"
                  : "Create Project"
                : `Continue ${currentStep + 1} of ${steps.length}`}
            </Button>
          </Box>
        )}
      </Box>
      <ExperienceDialog
        open={experienceModalOpen}
        onClose={() => setExperienceModalOpen(false)}
        rating={experienceDraftRating}
        onRatingChange={setExperienceDraftRating}
        notes={experienceDraftNotes}
        onNotesChange={setExperienceDraftNotes}
        onSave={saveExperience}
        hasExperience={Boolean(formData.experience)}
      />
      <PetsDialog
        open={petsModalOpen}
        onClose={() => setPetsModalOpen(false)}
        petDrafts={petDrafts}
        onPetDraftsChange={setPetDrafts}
        onSave={savePets}
      />
      <HouseholdDialog
        open={householdModalOpen}
        onClose={() => setHouseholdModalOpen(false)}
        adultsCount={householdDraft.adults}
        kidsCount={householdDraft.kids}
        kidsAgeValues={householdDraft.kidsAges}
        onAdjustHouseholdCount={adjustHouseholdCount}
        onAdjustKidsAge={adjustKidsAge}
        onSave={saveHousehold}
      />
    </Box>
  );
};

export default HomeownerProjectCreatePage;
